import React from 'react'
import { Link } from 'react-router-dom'
import { resolveApiAssetUrl } from '../../lib/api'
import { filterMonthlyBriefImagesByKind, sortMonthlyBriefImagesBySlideOrder } from '../../lib/monthlyBriefCardNews'

function getYoutubeEmbedUrl(rawUrl) {
  const source = String(rawUrl || '').trim()
  if (!source) return ''
  try {
    const url = source.startsWith('http://') || source.startsWith('https://')
      ? new URL(source)
      : new URL(`https://${source}`)
    const host = url.hostname.replace(/^www\./, '').toLowerCase()
    let videoId = ''
    if (host === 'youtu.be') {
      videoId = url.pathname.split('/').filter(Boolean)[0] || ''
    } else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      if (url.pathname === '/watch') videoId = url.searchParams.get('v') || ''
      else if (url.pathname.startsWith('/embed/')) videoId = url.pathname.split('/')[2] || ''
      else if (url.pathname.startsWith('/shorts/')) videoId = url.pathname.split('/')[2] || ''
      else if (url.pathname.startsWith('/live/')) videoId = url.pathname.split('/')[2] || ''
    }
    return videoId ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?rel=0&modestbranding=1` : ''
  } catch {
    return ''
  }
}

function usePersonalLandingInView({ threshold = 0.12, once = true, rootMargin = '0px 0px -10% 0px' } = {}) {
  const ref = React.useRef(null)
  const [visible, setVisible] = React.useState(false)
  React.useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return undefined
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          if (once) observer.unobserve(entry.target)
        } else if (!once) setVisible(false)
      },
      { threshold, rootMargin }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [once, rootMargin, threshold])
  return [ref, visible]
}

function formatAnimatedStatValue(amount, decimals) {
  return new Intl.NumberFormat('ko-KR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

function StatCountUpValue({ value, active }) {
  const raw = String(value || '')
  const parsed = React.useMemo(() => {
    const match = raw.match(/^([^0-9-]*)(-?\d[\d,]*(?:\.\d+)?)(.*)$/)
    if (!match) return null
    return {
      prefix: match[1],
      numericText: match[2],
      suffix: match[3],
      decimals: (match[2].split('.')[1] || '').length,
      target: Number(match[2].replace(/,/g, '')),
    }
  }, [raw])
  const [display, setDisplay] = React.useState(() => (parsed ? `${parsed.prefix}0${parsed.suffix}` : raw))
  React.useEffect(() => {
    if (!parsed) {
      setDisplay(raw)
      return undefined
    }
    if (!active) {
      setDisplay(`${parsed.prefix}0${parsed.suffix}`)
      return undefined
    }
    if (Number.isNaN(parsed.target)) {
      setDisplay(raw)
      return undefined
    }
    const duration = 1400
    const start = performance.now()
    let raf = 0
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = parsed.target * eased
      setDisplay(`${parsed.prefix}${formatAnimatedStatValue(current, parsed.decimals)}${parsed.suffix}`)
      if (progress < 1) raf = window.requestAnimationFrame(tick)
      else setDisplay(raw)
    }
    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [active, parsed, raw])
  return display
}

function AnimatedPersonalSection({ id, children }) {
  const [ref, visible] = usePersonalLandingInView({ threshold: 0.12 })
  return (
    <div
      ref={ref}
      id={id}
      className={`scroll-mt-24 transform-gpu transition-all duration-1000 ease-out ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
    >
      {typeof children === 'function' ? children(visible) : children}
    </div>
  )
}

function PersonalSectionContainer({ section, ctx, shell = 'default', children }) {
  const isOrg = ctx.layoutVariant === 'org'
  const id = `ps-${section.id}`
  const pc = ctx.primaryColor || '#0f172a'
  if (!isOrg) {
    return <div key={section.id} className="mt-8 rounded-2xl border p-6">{children}</div>
  }
  if (shell === 'stats') {
    return (
      <AnimatedPersonalSection key={section.id} id={id}>
        {(vis) => (
          <section className="relative overflow-hidden bg-slate-950 px-4 py-16 text-white md:px-6 md:py-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(96,165,250,0.12),transparent_28%)]" />
            <div className="org-float pointer-events-none absolute left-[10%] top-[18%] h-24 w-24 rounded-full bg-white/5 blur-3xl" />
            <div
              className="org-float pointer-events-none absolute bottom-[12%] right-[9%] h-36 w-36 rounded-full bg-sky-300/10 blur-3xl"
              style={{ animationDelay: '1s' }}
            />
            <div className="relative mx-auto max-w-6xl">{typeof children === 'function' ? children(vis) : children}</div>
          </section>
        )}
      </AnimatedPersonalSection>
    )
  }
  if (shell === 'contact') {
    return (
      <AnimatedPersonalSection key={section.id} id={id}>
        {() => (
          <section className="bg-slate-50 px-6 py-16 md:py-20">
            <div className="mx-auto max-w-3xl rounded-[32px] bg-white p-8 text-center shadow-sm ring-1 ring-slate-100 md:p-12">
              {typeof children === 'function' ? children(true) : children}
            </div>
          </section>
        )}
      </AnimatedPersonalSection>
    )
  }
  if (shell === 'cta') {
    return (
      <AnimatedPersonalSection key={section.id} id={id}>
        {(vis) => (
          <section className="bg-slate-900 px-6 py-16 text-white md:py-20">
            <div className={`relative mx-auto max-w-6xl transition-all duration-1000 ${vis ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`}>
              {typeof children === 'function' ? children(vis) : children}
            </div>
          </section>
        )}
      </AnimatedPersonalSection>
    )
  }
  return (
    <AnimatedPersonalSection key={section.id} id={id}>
      {(vis) => (
        <section className="bg-white px-6 py-16 md:py-20">
          <div className={`relative mx-auto max-w-6xl transition-all duration-1000 ${vis ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            {typeof children === 'function' ? children(vis) : children}
          </div>
          <div
            className={`mx-auto mt-12 h-px max-w-6xl transition-all duration-700 ${vis ? 'scale-x-100 opacity-100' : 'scale-x-90 opacity-0'}`}
            style={{ backgroundColor: `${pc}33` }}
          />
        </section>
      )}
    </AnimatedPersonalSection>
  )
}

function IconButton({ label, href, onClick, disabled, children }) {
  const cls = disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'
  if (href && !disabled) {
    return (
      <a
        className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg ${cls}`}
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noreferrer' : undefined}
        onClick={disabled ? (e) => e.preventDefault() : undefined}
      >
        {children}
        <span className="text-[11px] text-slate-600">{label}</span>
      </a>
    )
  }
  return (
    <button
      type="button"
      className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg ${cls}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {children}
      <span className="text-[11px] text-slate-600">{label}</span>
    </button>
  )
}

function Svg({ children, className = '' }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={`h-5 w-5 ${className}`}>
      {children}
    </svg>
  )
}

function BrandIcon({ kind }) {
  if (kind === 'youtube') {
    return (
      <svg viewBox="0 0 28 20" aria-hidden="true" className="h-5 w-7">
        <rect x="0" y="0" width="28" height="20" rx="5" fill="#FF0000" />
        <path d="M12 6.2L19 10L12 13.8V6.2Z" fill="#fff" />
      </svg>
    )
  }
  if (kind === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
        <defs>
          <linearGradient id="ig" x1="0" y1="24" x2="24" y2="0" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FEDA75" />
            <stop offset="0.35" stopColor="#FA7E1E" />
            <stop offset="0.7" stopColor="#D62976" />
            <stop offset="1" stopColor="#4F5BD5" />
          </linearGradient>
        </defs>
        <rect x="3" y="3" width="18" height="18" rx="5" fill="url(#ig)" />
        <rect x="7" y="7" width="10" height="10" rx="3" stroke="#fff" strokeWidth="1.8" fill="none" />
        <circle cx="12" cy="12" r="2.4" stroke="#fff" strokeWidth="1.8" fill="none" />
        <circle cx="16.8" cy="7.2" r="1.1" fill="#fff" />
      </svg>
    )
  }
  if (kind === 'blog') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
        <rect x="3" y="3" width="18" height="18" rx="5" fill="#03C75A" />
        <path
          d="M8 8.2h4.4c2.3 0 3.9 1.2 3.9 3.2 0 2.1-1.6 3.3-3.9 3.3H10.7V18H8V8.2Zm2.7 2.3v2.1h1.6c.9 0 1.5-.4 1.5-1.1 0-.7-.6-1-1.5-1h-1.6Z"
          fill="#fff"
        />
      </svg>
    )
  }
  return null
}

/** 히어로 좌우 분할용 — 프로필+갤러리 슬라이드 */
function HeroSplitImageCarousel({ urls }) {
  const list = Array.isArray(urls) ? urls.filter(Boolean).slice(0, 8) : []
  const [idx, setIdx] = React.useState(0)

  React.useEffect(() => {
    if (list.length <= 1) return
    const t = setInterval(() => setIdx((v) => (v + 1) % list.length), 4500)
    return () => clearInterval(t)
  }, [list.length])

  React.useEffect(() => {
    if (idx >= list.length) setIdx(0)
  }, [idx, list.length])

  if (list.length === 0) {
    return (
      <div className="flex h-full min-h-[280px] w-full items-center justify-center bg-white/10 px-6 text-center text-sm text-white/55">
        프로필 또는 소개 갤러리 사진을 등록하면
        <br />
        이 영역에 슬라이드로 표시됩니다.
      </div>
    )
  }

  return (
    <div className="relative h-full min-h-[280px] w-full">
      <img
        src={resolveApiAssetUrl(list[idx])}
        alt=""
        className="h-full w-full object-cover object-center"
        decoding="async"
        loading="eager"
        onError={(e) => {
          try {
            e.currentTarget.style.display = 'none'
          } catch {
            /* noop */
          }
        }}
      />
      {list.length > 1 ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`h-2 w-2 rounded-full transition ${i === idx ? 'bg-white scale-110' : 'bg-white/45 hover:bg-white/70'}`}
                onClick={() => setIdx(i)}
                aria-label={`사진 ${i + 1}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}

function LandingImageCarousel({ urls }) {
  const list = Array.isArray(urls) ? urls.filter(Boolean).slice(0, 5) : []
  const [idx, setIdx] = React.useState(0)

  React.useEffect(() => {
    if (list.length <= 1) return
    const t = setInterval(() => {
      setIdx((v) => (v + 1) % list.length)
    }, 4000)
    return () => clearInterval(t)
  }, [list.length])

  React.useEffect(() => {
    if (idx >= list.length) setIdx(0)
  }, [idx, list.length])

  if (list.length === 0) {
    return <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-100" />
  }

  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0 p-3">
        <img
          src={resolveApiAssetUrl(list[idx])}
          alt=""
          className="h-full w-full object-contain object-center"
          decoding="async"
          loading="eager"
          onError={(e) => {
            try {
              e.currentTarget.style.display = 'none'
            } catch {
              /* noop */
            }
          }}
        />
      </div>

      {list.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1">
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`h-1.5 w-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/50'}`}
              onClick={() => setIdx(i)}
              aria-label={`이동 ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function BodyGalleryCarousel({ urls }) {
  const list = Array.isArray(urls) ? urls.filter(Boolean).slice(0, 6) : []
  const [idx, setIdx] = React.useState(0)
  React.useEffect(() => {
    if (list.length <= 1) return
    const t = setInterval(() => setIdx((v) => (v + 1) % list.length), 4500)
    return () => clearInterval(t)
  }, [list.length])
  React.useEffect(() => {
    if (idx >= list.length) setIdx(0)
  }, [idx, list.length])
  if (list.length === 0) return null
  return (
    <div className="relative mt-4 aspect-video w-full overflow-hidden rounded-xl border bg-slate-50">
      <img
        src={resolveApiAssetUrl(list[idx])}
        alt=""
        className="h-full w-full object-contain object-center p-2"
        loading="lazy"
        decoding="async"
      />
      {list.length > 1 ? (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`h-2 w-2 rounded-full ${i === idx ? 'bg-slate-800' : 'bg-slate-300'}`}
              onClick={() => setIdx(i)}
              aria-label={`슬라이드 ${i + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function dedupeOrgParts(parts) {
  const out = []
  const seen = new Set()
  for (const p of parts || []) {
    const v = p == null ? '' : String(p).trim()
    if (!v) continue
    if (seen.has(v)) continue
    seen.add(v)
    out.push(v)
  }
  return out
}

function OrgHeroIconButton({ label, href, onClick, disabled, children }) {
  const cls = disabled ? 'opacity-35 cursor-not-allowed' : 'hover:bg-white/10'
  if (href && !disabled) {
    return (
      <a
        className={`flex flex-col items-center justify-center gap-1 rounded-xl px-3 py-2 text-white/90 ${cls}`}
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noreferrer' : undefined}
      >
        {children}
        <span className="text-[10px] font-medium text-white/70">{label}</span>
      </a>
    )
  }
  return (
    <button type="button" className={`flex flex-col items-center justify-center gap-1 rounded-xl px-3 py-2 text-white/90 ${cls}`} onClick={disabled ? undefined : onClick} disabled={disabled}>
      {children}
      <span className="text-[10px] font-medium text-white/70">{label}</span>
    </button>
  )
}

function PersonalHeroVideoEmbed({ url, title, isVisible, className = '' }) {
  const embedUrl = getYoutubeEmbedUrl(url)
  if (!embedUrl) return null
  return (
    <div
      className={`mt-10 overflow-hidden rounded-[28px] border border-white/15 bg-white/10 p-2 shadow-[0_20px_60px_rgba(0,0,0,0.25)] backdrop-blur-sm transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} ${className}`}
    >
      <div className="aspect-video overflow-hidden rounded-[22px] bg-slate-950">
        <iframe
          src={embedUrl}
          title={title || 'YouTube'}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </div>
  )
}

/**
 * 풀뷰포트 히어로: 중앙 기준 좌우 50/50 — 왼쪽(또는 split 템플릿 시 오른쪽) 이미지 슬라이드, 반대편에 이름·소속·CTA·SNS.
 */
export function PersonalLandingOrgHero({
  heroSection,
  data,
  profileImageUrl,
  landingGalleryEnabled,
  landingGalleryUrls,
  youtubeUrl,
  instagramUrl,
  blogUrl,
  phoneDigits,
  smsHref,
  primaryColor,
  isPreview,
  onPrimaryCta,
}) {
  const content = heroSection?.content && typeof heroSection.content === 'object' ? heroSection.content : {}
  const heroOff = heroSection && heroSection.enabled === false
  const pc = primaryColor || '#0f172a'
  const name = data?.name || ''
  const org = dedupeOrgParts([data?.orgRootName, data?.orgParentName, data?.orgName]).join(' ')
  const jobTitle = data?.title || ''
  const [heroVisRef, heroVisible] = usePersonalLandingInView({ threshold: 0.08, once: false })

  const heroSlideIds = React.useMemo(() => {
    const profileId = profileImageUrl ? String(profileImageUrl).trim() : ''
    const g = landingGalleryEnabled && Array.isArray(landingGalleryUrls)
      ? landingGalleryUrls.filter(Boolean).map((id) => String(id).trim()).filter(Boolean).slice(0, 8)
      : []
    const seen = new Set()
    const out = []
    const push = (id) => {
      if (!id || seen.has(id)) return
      seen.add(id)
      out.push(id)
    }
    push(profileId)
    g.forEach(push)
    return out
  }, [landingGalleryEnabled, landingGalleryUrls, profileImageUrl])

  /** personal-hero.split-v1: 사진 열을 오른쪽(데스크톱)에 배치 */
  const imageOnRight = String(heroSection?.templateCode || '') === 'personal-hero.split-v1'
  const videoUrl = content.youtubeUrl || youtubeUrl || ''

  if (heroOff) {
    return (
      <section className="relative min-h-[40vh] overflow-hidden bg-slate-100 px-6 pt-24 pb-12 text-slate-600 md:pt-28">
        <div className="mx-auto max-w-6xl text-center text-sm">상단 소개 섹션이 비활성화되어 있습니다.</div>
      </section>
    )
  }

  const photoColOrder = imageOnRight ? 'order-1 md:order-2' : 'order-1 md:order-1'
  const textColOrder = imageOnRight ? 'order-2 md:order-1' : 'order-2 md:order-2'

  return (
    <section
      className="relative min-h-[88vh] overflow-hidden pt-20 text-white md:min-h-screen md:pt-24"
      style={{ backgroundColor: pc }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.12),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(125,211,252,0.16),transparent_32%)]" />
      <div className="org-float pointer-events-none absolute left-[8%] top-[22%] h-32 w-32 rounded-full bg-white/8 blur-3xl" />
      <div
        className="org-float pointer-events-none absolute bottom-[14%] right-[10%] h-44 w-44 rounded-full bg-sky-300/20 blur-3xl"
        style={{ animationDelay: '1.2s' }}
      />

      <div
        ref={heroVisRef}
        className="relative z-10 mx-auto flex min-h-[calc(88vh-80px)] max-w-6xl flex-col justify-center px-4 py-12 md:min-h-[calc(100vh-96px)] md:px-6 md:py-16"
      >
        <div className="grid w-full grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-12 lg:gap-14">
          <div className={`${photoColOrder} w-full`}>
            <div
              className={`mx-auto w-full max-w-md overflow-hidden rounded-[28px] border border-white/20 bg-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.25)] md:max-w-none ${heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} transition-all duration-1000`}
            >
              <div className="aspect-[4/5] w-full md:aspect-[3/4] md:max-h-[min(72vh,560px)]">
                <HeroSplitImageCarousel urls={heroSlideIds} />
              </div>
            </div>
          </div>

          <div className={`${textColOrder} w-full text-left`}>
            <p
              className={`mb-3 whitespace-pre-line text-xs font-semibold uppercase tracking-[0.35em] text-white/75 md:text-sm ${heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'} transition-all duration-700`}
            >
              {content.eyebrow || '설계 EZ'}
            </p>
            <h1
              className={`whitespace-pre-line text-3xl font-black leading-[1.12] sm:text-4xl md:text-5xl lg:text-6xl ${heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'} transition-all duration-1000 delay-75`}
            >
              {content.title ? content.title : [name || '소개페이지', jobTitle].filter(Boolean).join('\n')}
            </h1>
            {org ? (
              <p
                className={`mt-4 text-base font-semibold text-white/90 md:text-lg ${heroVisible ? 'opacity-100' : 'opacity-0'} transition-all delay-100 duration-700`}
              >
                {org}
              </p>
            ) : null}
            {(content.subtitle || data?.bioShort || data?.introText) ? (
              <p
                className={`mt-5 whitespace-pre-line text-base leading-relaxed text-white/85 md:text-lg ${heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'} transition-all duration-1000 delay-150`}
              >
                {content.subtitle || [data?.bioShort, data?.introText].filter(Boolean).join('\n\n')}
              </p>
            ) : null}

            <div className="mt-8 flex flex-wrap gap-3 md:mt-10">
              <button
                type="button"
                onClick={() => {
                  if (!isPreview && typeof onPrimaryCta === 'function') onPrimaryCta()
                }}
                disabled={isPreview}
                className={`rounded-full bg-white px-7 py-3.5 text-base font-semibold text-slate-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100 md:px-9 md:py-4 ${heroVisible ? 'opacity-100' : 'opacity-0'} duration-1000 delay-200 ${isPreview ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {content.ctaText || '상담 문의하기'}
              </button>
            </div>

            <div
              className={`mt-6 flex max-w-full flex-wrap items-center gap-1 rounded-2xl border border-white/15 bg-white/5 p-2 backdrop-blur-sm md:mt-8 ${heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'} transition-all duration-1000 delay-300`}
            >
              <OrgHeroIconButton label="YouTube" href={youtubeUrl} disabled={!youtubeUrl}>
                <BrandIcon kind="youtube" />
              </OrgHeroIconButton>
              <OrgHeroIconButton label="Instagram" href={instagramUrl} disabled={!instagramUrl}>
                <BrandIcon kind="instagram" />
              </OrgHeroIconButton>
              <OrgHeroIconButton label="Blog" href={blogUrl} disabled={!blogUrl}>
                <BrandIcon kind="blog" />
              </OrgHeroIconButton>
              <OrgHeroIconButton label="전화" href={phoneDigits ? `tel:${phoneDigits}` : ''} disabled={!phoneDigits}>
                <Svg className="text-white">
                  <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.8 2.1Z" />
                </Svg>
              </OrgHeroIconButton>
              <OrgHeroIconButton label="문자" href={smsHref} disabled={!phoneDigits}>
                <Svg className="text-white">
                  <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />
                </Svg>
              </OrgHeroIconButton>
            </div>

            <PersonalHeroVideoEmbed url={videoUrl} title={name || 'hero'} isVisible={heroVisible} className="max-w-full" />
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * 상단 히어로(명함형). templateCode에 따라 레이아웃·밀도만 분기.
 */
export function PersonalLandingHero({
  heroSection,
  data,
  profileImageUrl,
  landingGalleryEnabled,
  landingGalleryUrls,
  youtubeUrl,
  instagramUrl,
  blogUrl,
  phoneDigits,
  smsHref,
}) {
  const templateCode = heroSection?.templateCode || 'personal-hero.centered-v1'
  const heroOff = heroSection && heroSection.enabled === false
  const content = heroSection?.content && typeof heroSection.content === 'object' ? heroSection.content : {}

  const name = data?.name || ''
  const org = dedupeOrgParts([data?.orgRootName, data?.orgParentName, data?.orgName]).join(' ')
  const title = data?.title || '팀장'

  const isSplit = templateCode === 'personal-hero.split-v1'
  const isMinimal = templateCode === 'personal-hero.minimal-v1'

  const rowCls = isSplit ? 'flex flex-col md:flex-row-reverse items-stretch gap-5' : 'flex flex-col md:flex-row items-stretch gap-5'
  const photoMinH = isMinimal ? 'min-h-[140px] md:min-h-[180px]' : 'min-h-[220px] md:min-h-[260px]'
  const customHeadline = typeof content.title === 'string' && content.title.trim()
  const titleCls = customHeadline
    ? (isMinimal ? 'mt-1 text-xl md:text-2xl font-extrabold tracking-tight whitespace-pre-line' : 'mt-2 text-2xl md:text-4xl font-extrabold tracking-tight whitespace-pre-line')
    : (isMinimal ? 'mt-1 text-xl md:text-2xl font-extrabold tracking-tight truncate' : 'mt-2 text-2xl md:text-4xl font-extrabold tracking-tight truncate')

  if (heroOff) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-3 text-center text-xs text-slate-500">
        상단 소개 섹션이 비활성화되어 있습니다. 편집 화면에서 다시 켤 수 있습니다.
      </div>
    )
  }

  return (
    <div className="rounded-2xl border bg-gradient-to-b from-slate-50 to-white p-8">
      <div className={rowCls}>
        <div className="flex-1">
          <div className={`relative w-full ${photoMinH} rounded-2xl bg-slate-200 overflow-hidden border self-stretch`}>
            {landingGalleryEnabled && landingGalleryUrls.length > 0 ? (
              <LandingImageCarousel urls={landingGalleryUrls} />
            ) : (
              <>
                {profileImageUrl ? (
                  <div className="absolute inset-0 p-3">
                    <img
                      src={resolveApiAssetUrl(profileImageUrl)}
                      alt=""
                      className="h-full w-full object-contain object-center"
                      decoding="async"
                      loading="eager"
                      onError={(e) => {
                        try {
                          e.currentTarget.style.display = 'none'
                        } catch {
                          /* noop */
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-slate-200 to-slate-100" />
                )}
              </>
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className={`${isMinimal ? 'text-[11px] text-slate-500' : 'text-sm text-slate-500'} whitespace-pre-line`}>
            {content.eyebrow || '설계 EZ'}
          </div>
          <h1 className={titleCls}>
            {customHeadline ? (
              content.title
            ) : (
              <>
                {name || '소개페이지'}
                {title ? <span className="ml-2 text-base md:text-xl font-semibold text-slate-600">{title}</span> : null}
              </>
            )}
          </h1>
          {org && <div className={`mt-2 text-slate-600 truncate ${isMinimal ? 'text-xs md:text-sm' : 'text-sm md:text-base'}`}>{org}</div>}

          {(content.subtitle || data?.bioShort || data?.introText) && (
            <div className={isMinimal ? 'mt-3 space-y-1' : 'mt-4 space-y-2'}>
              {content.subtitle ? (
                <div className={`text-slate-700 whitespace-pre-line ${isMinimal ? 'text-xs md:text-sm' : 'text-sm md:text-base'}`}>
                  {content.subtitle}
                </div>
              ) : (
                <>
                  {data?.bioShort && (
                    <div className={`text-slate-800 font-semibold whitespace-pre-line ${isMinimal ? 'text-sm' : 'text-base'}`}>{data.bioShort}</div>
                  )}
                  {data?.introText && (
                    <div className={`text-slate-700 whitespace-pre-line ${isMinimal ? 'text-xs md:text-sm' : 'text-sm md:text-base'}`}>
                      {data.introText}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 rounded-xl border bg-white shadow-sm p-3 flex items-center justify-between">
        <IconButton label="YouTube" href={youtubeUrl} disabled={!youtubeUrl}>
          <BrandIcon kind="youtube" />
        </IconButton>
        <IconButton label="Instagram" href={instagramUrl} disabled={!instagramUrl}>
          <BrandIcon kind="instagram" />
        </IconButton>
        <IconButton label="Blog" href={blogUrl} disabled={!blogUrl}>
          <BrandIcon kind="blog" />
        </IconButton>
        <IconButton label="전화" href={phoneDigits ? `tel:${phoneDigits}` : ''} disabled={!phoneDigits}>
          <Svg className="text-slate-700">
            <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.8 2.1Z" />
          </Svg>
        </IconButton>
        <IconButton label="문자" href={smsHref} disabled={!phoneDigits}>
          <Svg className="text-slate-700">
            <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />
          </Svg>
        </IconButton>
      </div>
    </div>
  )
}

/**
 * @param {object} ctx — PublicLanding 페이지 컨텍스트(데이터·핸들러)
 */
export function renderPersonalLandingBodySection(section, ctx) {
  if (!section) return null
  if (section.enabled === false && section.type !== 'ad-review') return null
  const content = section?.content && typeof section.content === 'object' ? section.content : {}
  const templateCode = section.templateCode || ''
  const org = ctx.layoutVariant === 'org'
  const tTitle = org ? 'text-2xl font-black tracking-tight text-slate-900 md:text-4xl' : 'text-sm font-semibold'
  const tBody = org ? 'mt-6 whitespace-pre-line text-base leading-8 text-slate-700 md:text-lg' : 'mt-2 text-sm text-slate-700 whitespace-pre-line'
  const tMuted = org ? 'mt-4 text-base text-slate-500' : 'mt-3 text-sm text-slate-500'
  const eyebrow = (label) => (org ? (
    <div className="mb-3 inline-flex rounded-full bg-slate-100 px-4 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
      {label}
    </div>
  ) : null)

  switch (section.type) {
    case 'about-profile': {
      const cardLike = templateCode === 'personal-about.card-v1' && !org
      const inner = (
        <>
          {eyebrow('Profile')}
          <div className={tTitle}>{content.title || '프로필 소개'}</div>
          <div className={tBody}>
            {content.body || ctx.data?.introText || '소개 내용을 입력해 주세요.'}
          </div>
        </>
      )
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {cardLike ? <div className="rounded-xl border bg-slate-50/80 p-4">{inner}</div> : inner}
        </PersonalSectionContainer>
      )
    }
    case 'service-tags': {
      const cardItems = Array.isArray(content?.items) ? content.items.filter((it) => it?.title) : []
      const useCards = templateCode === 'personal-services.cards-v1' && cardItems.length > 0
      const chipCls = org
        ? 'inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 shadow-sm'
        : 'inline-flex items-center rounded-full bg-slate-50 border px-3 py-1 text-xs text-slate-700'
      const body = (
        <>
          {eyebrow('Services')}
          <div className={tTitle}>{content.title || '제공 서비스'}</div>
          {useCards ? (
            <div className={`${org ? 'mt-8' : 'mt-4'} grid grid-cols-1 gap-4 sm:grid-cols-2`}>
              {cardItems.map((item, index) => (
                <div
                  key={`${item?.title || 'svc'}-${index}`}
                  className={org ? 'rounded-3xl border border-slate-200 bg-white p-6 shadow-sm' : 'rounded-xl border bg-slate-50 px-4 py-3'}
                >
                  <div className={org ? 'text-lg font-bold text-slate-900' : 'text-sm font-semibold text-slate-800'}>{item.title}</div>
                  {item.body ? <div className={`${org ? 'mt-2 text-sm leading-7 text-slate-600' : 'mt-1 text-xs text-slate-600'} whitespace-pre-line`}>{item.body}</div> : null}
                </div>
              ))}
            </div>
          ) : ctx.tags.length > 0 ? (
            <div className={`${org ? 'mt-8' : 'mt-4'} flex flex-wrap gap-2`}>
              {ctx.tags.map((tag) => (
                <span key={tag} className={chipCls}>
                  {tag}
                </span>
              ))}
            </div>
          ) : cardItems.length === 0 ? (
            <div className={tMuted}>서비스 태그를 아직 입력하지 않았습니다.</div>
          ) : (
            <div className={tMuted}>카드 내용을 입력해 주세요.</div>
          )}
        </>
      )
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {body}
        </PersonalSectionContainer>
      )
    }
    case 'proof-stats': {
      const items = Array.isArray(content?.items) ? content.items.filter(Boolean) : []
      if (org) {
        return (
          <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="stats">
            {(vis) => (
              <>
                {content.title ? (
                  <h2 className={`mb-10 text-center text-3xl font-black md:text-5xl transition-all duration-1000 ${vis ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                    {content.title}
                  </h2>
                ) : null}
                {items.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
                    {items.map((item, index) => (
                      <div
                        key={`${item?.label || item?.title || 'item'}-${index}`}
                        className={`group rounded-3xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur-sm transition-all duration-700 hover:-translate-y-1 hover:bg-white/10 ${vis ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
                        style={{ transitionDelay: `${index * 90 + 80}ms` }}
                      >
                        <div className="text-sm font-medium text-white/65">{item?.label || item?.title || '지표'}</div>
                        <div className="mt-3 text-2xl font-black text-white drop-shadow-[0_0_24px_rgba(255,255,255,0.12)] md:text-5xl">
                          <StatCountUpValue value={item?.value || item?.body || '-'} active={vis} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-sm text-white/60">지표를 아직 입력하지 않았습니다.</div>
                )}
              </>
            )}
          </PersonalSectionContainer>
        )
      }
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          <div className={tTitle}>{content.title || '신뢰를 만드는 지표'}</div>
          {items.length > 0 ? (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {items.map((item, index) => (
                <div key={`${item?.label || item?.title || 'item'}-${index}`} className="rounded-xl border bg-slate-50 px-4 py-3">
                  <div className="text-[11px] text-slate-500">{item?.label || item?.title || '지표'}</div>
                  <div className="mt-1 text-base font-semibold text-slate-800">{item?.value || item?.body || '-'}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={tMuted}>지표를 아직 입력하지 않았습니다.</div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'monthly-issue': {
      const payload = ctx.monthlyIssue.brief?.payload
      const lifeList = sortMonthlyBriefImagesBySlideOrder(filterMonthlyBriefImagesByKind(ctx.monthlyIssue.images, 'life'), payload, 'life')
      const fireList = sortMonthlyBriefImagesBySlideOrder(filterMonthlyBriefImagesByKind(ctx.monthlyIssue.images, 'fire'), payload, 'fire')
      const periodLabel =
        ctx.monthlyIssue?.brief?.year && ctx.monthlyIssue?.brief?.month
          ? `${ctx.monthlyIssue.brief.year}.${String(ctx.monthlyIssue.brief.month).padStart(2, '0')}`
          : null

      const thumbStrip = (kind, list) => {
        if (list.length === 0) {
          return <div className="text-sm text-slate-500">등록된 카드뉴스가 없습니다.</div>
        }
        return (
          <div className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:thin]">
            {list.map((img, idx) => (
              <button
                key={`${kind}-${img?.imageName || idx}`}
                type="button"
                className="shrink-0 w-[min(42vw,168px)] snap-start overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm outline-none transition hover:ring-2 hover:ring-slate-400 focus-visible:ring-2 focus-visible:ring-brand-navy"
                onClick={() => ctx.openPublicIssueModal(kind, idx)}
              >
                {img?.fileId ? (
                  <img
                    src={resolveApiAssetUrl(img.fileId)}
                    alt=""
                    className="aspect-[4/5] w-full object-cover object-center"
                    loading="lazy"
                    decoding="async"
                  />
                ) : null}
              </button>
            ))}
          </div>
        )
      }

      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {periodLabel ? (
            <div className={`mb-4 flex justify-end ${org ? 'text-sm text-slate-500' : 'text-[11px] text-slate-500'}`}>{periodLabel} 기준</div>
          ) : null}
          {ctx.monthlyIssue.loading ? (
            <div className={tMuted}>카드뉴스를 불러오는 중입니다.</div>
          ) : lifeList.length === 0 && fireList.length === 0 ? (
            <div className={tMuted}>현재 노출 가능한 카드뉴스가 없습니다.</div>
          ) : (
            <div className={`${org ? 'mt-2' : 'mt-0'} grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8`}>
              <div className={`${org ? 'rounded-3xl border border-indigo-100 bg-indigo-50/40 p-6 shadow-sm' : 'rounded-xl border border-indigo-100 bg-indigo-50/30 p-4'}`}>
                <div className={`${org ? 'text-lg font-black text-indigo-950' : 'text-sm font-bold text-indigo-950'} mb-3`}>생명보험</div>
                {thumbStrip('life', lifeList)}
              </div>
              <div className={`${org ? 'rounded-3xl border border-violet-100 bg-violet-50/40 p-6 shadow-sm' : 'rounded-xl border border-violet-100 bg-violet-50/30 p-4'}`}>
                <div className={`${org ? 'text-lg font-black text-violet-950' : 'text-sm font-bold text-violet-950'} mb-3`}>손해보험</div>
                {thumbStrip('fire', fireList)}
              </div>
            </div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'media-gallery':
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('Gallery')}
          <div className={tTitle}>{content.title || '미디어 갤러리'}</div>
          <div className={org ? 'mt-4 text-lg text-slate-600' : 'mt-2 text-sm text-slate-600'}>{content.body || '메인 슬라이드 이미지를 소개 자료로 노출합니다.'}</div>
          {ctx.landingGalleryEnabled && ctx.landingGalleryUrls.length > 0 ? (
            templateCode === 'personal-gallery.carousel-v1' ? (
              <div className={org ? 'mt-8' : ''}>
                <BodyGalleryCarousel urls={ctx.landingGalleryUrls} />
              </div>
            ) : (
              <div className={`${org ? 'mt-8' : 'mt-4'} grid grid-cols-2 gap-4 md:grid-cols-3`}>
                {ctx.landingGalleryUrls.slice(0, 6).map((url, idx) => (
                  <div key={`${url}-${idx}`} className={`overflow-hidden ${org ? 'rounded-3xl border border-slate-200 bg-slate-50 shadow-sm' : 'rounded-xl border bg-slate-50'}`}>
                    <img
                      src={resolveApiAssetUrl(url)}
                      alt=""
                      className={org ? 'h-40 w-full object-contain object-center p-3 md:h-48' : 'h-32 w-full object-contain object-center p-2'}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className={tMuted}>갤러리 이미지가 아직 없습니다.</div>
          )}
        </PersonalSectionContainer>
      )
    case 'image-card':
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          <div className={`overflow-hidden ${org ? 'rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]' : 'rounded-2xl border'}`}>
            {ctx.imageCardEnabled && ctx.imageCardUrl ? (
              <img
                src={resolveApiAssetUrl(ctx.imageCardUrl)}
                alt=""
                className="w-full object-contain object-center"
                decoding="async"
                loading="lazy"
                onError={(e) => {
                  try {
                    e.currentTarget.style.display = 'none'
                  } catch {
                    /* noop */
                  }
                }}
              />
            ) : (
              <div className="p-6 text-sm text-slate-500">이미지 카드가 아직 설정되지 않았습니다.</div>
            )}
          </div>
        </PersonalSectionContainer>
      )
    case 'career': {
      const lines = ctx.showCareerCard && ctx.career
        ? String(ctx.career).split('\n').map((l) => l.trim()).filter(Boolean)
        : []
      const timeline = templateCode === 'personal-timeline.v1' && lines.length > 0
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('Career')}
          <div className={tTitle}>경력</div>
          {ctx.showCareerCard && ctx.career ? (
            timeline ? (
              <ul className={`${org ? 'mt-8' : 'mt-4'} space-y-0 border-l-2 border-slate-200 pl-4`}>
                {lines.map((line, i) => (
                  <li key={i} className="relative pb-4 last:pb-0">
                    <span className="absolute -left-[calc(0.5rem+5px)] top-1.5 h-2.5 w-2.5 rounded-full bg-slate-400 ring-4 ring-white" />
                    <div className={org ? 'text-base text-slate-700' : 'text-sm text-slate-700'}>{line}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={tBody}>{ctx.career}</div>
            )
          ) : (
            <div className={tMuted}>경력 정보를 아직 입력하지 않았습니다.</div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'activity':
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('Area')}
          <div className={tTitle}>활동지역</div>
          {ctx.showActivityAreaCard && ctx.activity ? (
            <div className={tBody}>{ctx.activity}</div>
          ) : (
            <div className={tMuted}>활동지역 정보를 아직 입력하지 않았습니다.</div>
          )}
        </PersonalSectionContainer>
      )
    case 'free-text':
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('Story')}
          {content.title ? <div className={tTitle}>{content.title}</div> : null}
          {ctx.showFreeTextCard && ctx.freeText ? (
            <div className={tBody}>{ctx.freeText}</div>
          ) : (
            <div className={tMuted}>{content.body || '자유 소개 내용을 아직 입력하지 않았습니다.'}</div>
          )}
        </PersonalSectionContainer>
      )
    case 'customer-case': {
      const items = Array.isArray(content?.items) ? content.items.filter(Boolean) : []
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('Cases')}
          <div className={tTitle}>{content.title || '상담 사례'}</div>
          {items.length > 0 ? (
            <div className={`${org ? 'mt-8 space-y-4' : 'mt-3 space-y-3'}`}>
              {items.map((item, index) => (
                <div
                  key={`${item?.title || 'case'}-${index}`}
                  className={org ? 'rounded-3xl border border-slate-200 bg-white p-6 shadow-sm' : 'rounded-xl border bg-slate-50 px-4 py-3'}
                >
                  <div className={org ? 'text-lg font-bold text-slate-900' : 'text-sm font-semibold text-slate-800'}>{item?.title || '사례'}</div>
                  <div className={`${org ? 'mt-2 text-base leading-7' : 'mt-1 text-sm'} text-slate-700 whitespace-pre-line`}>{item?.body || '-'}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={tMuted}>상담 사례를 아직 입력하지 않았습니다.</div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'contact': {
      const centered = templateCode === 'personal-contact.centered-v1' || org
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell={org ? 'contact' : 'default'}>
          <div className={`${org ? 'text-3xl font-black text-slate-900 md:text-5xl' : 'text-sm font-semibold'} ${centered ? 'text-center' : ''}`}>
            문의 정보
          </div>
          {ctx.phone || ctx.email || ctx.mobileFaxNumber || ctx.data?.addressText || ctx.kakaoChatUrl ? (
            <>
              <div className={`${org ? 'mt-8 space-y-4 text-base md:text-lg' : 'mt-3 space-y-2 text-sm'} text-slate-700 ${centered ? 'text-center' : ''}`}>
                {ctx.phone && (
                  <div className={`flex items-center gap-2 ${centered ? 'justify-center' : ''}`}>
                    <span className="text-slate-500">전화</span>
                    <span className="font-medium">{ctx.phone}</span>
                  </div>
                )}
                {ctx.email && (
                  <div className={`flex items-center gap-2 ${centered ? 'justify-center' : ''}`}>
                    <span className="text-slate-500">이메일</span>
                    <span className="break-all font-medium">{ctx.email}</span>
                  </div>
                )}
                {ctx.mobileFaxNumber && (
                  <div className={`flex items-center gap-2 ${centered ? 'justify-center' : ''}`}>
                    <span className="text-slate-500">모바일 팩스</span>
                    <span className="font-medium">{ctx.mobileFaxNumber}</span>
                  </div>
                )}
                {ctx.data?.addressText && (
                  <div className={`flex items-start gap-2 ${centered ? 'justify-center text-center' : ''}`}>
                    <span className="mt-0.5 text-slate-500">주소</span>
                    <span className="whitespace-pre-line font-medium">{ctx.data.addressText}</span>
                  </div>
                )}
              </div>
              {ctx.kakaoChatUrl && (
                <div className={`${org ? 'mt-6' : 'mt-4'} ${centered ? 'flex justify-center' : ''}`}>
                  <a
                    className={`inline-flex px-4 py-2 text-sm font-semibold ${org ? 'rounded-full bg-[#fde047] text-slate-900 hover:opacity-95' : 'rounded-lg border hover:bg-slate-50'}`}
                    href={ctx.kakaoChatUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    카카오 상담 바로가기
                  </a>
                </div>
              )}
            </>
          ) : (
            <div className={org ? 'mt-6 text-slate-500' : 'mt-3 text-sm text-slate-500'}>문의 정보가 아직 입력되지 않았습니다.</div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'credentials': {
      const items = Array.isArray(content?.items) ? content.items.filter((it) => it?.title) : []
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('Credentials')}
          <div className={tTitle}>{content.title || '자격 및 이력'}</div>
          {items.length > 0 ? (
            <ul className={`${org ? 'mt-8' : 'mt-4'} space-y-2`}>
              {items.map((item, index) => (
                <li
                  key={`${item?.title}-${index}`}
                  className={`flex flex-wrap items-baseline gap-2 border-b border-slate-100 pb-3 last:border-0 ${org ? 'text-base text-slate-700' : 'text-sm text-slate-700'}`}
                >
                  <span className="font-semibold text-slate-800">{item.title}</span>
                  {item.subtitle ? <span className="text-slate-600">{item.subtitle}</span> : null}
                  {item.year ? <span className={`${org ? 'text-sm' : 'text-xs'} text-slate-500 ml-auto`}>{item.year}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <div className={tMuted}>항목을 입력해 주세요.</div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'values': {
      const items = Array.isArray(content?.items) ? content.items.filter((it) => it?.title) : []
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('Promise')}
          <div className={tTitle}>{content.title || '고객을 향한 약속'}</div>
          {items.length > 0 ? (
            <div className={`${org ? 'mt-8' : 'mt-4'} grid grid-cols-1 gap-4 md:grid-cols-3`}>
              {items.slice(0, 6).map((item, index) => (
                <div
                  key={`${item?.title}-${index}`}
                  className={org ? 'rounded-3xl border border-slate-200 bg-white p-6 shadow-sm' : 'rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-4 py-4'}
                >
                  <div className={org ? 'text-lg font-bold text-slate-900' : 'text-sm font-bold text-slate-900'}>{item.title}</div>
                  <div className={`${org ? 'mt-3 text-sm leading-7' : 'mt-2 text-xs leading-relaxed'} text-slate-600 whitespace-pre-line`}>{item.body || ''}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={tMuted}>약속 카드를 입력해 주세요.</div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'faq': {
      const items = Array.isArray(content?.items) ? content.items.filter((it) => it?.q) : []
      return (
        <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="default">
          {eyebrow('FAQ')}
          <div className={tTitle}>{content.title || '자주 묻는 질문'}</div>
          {items.length > 0 ? (
            <div className={`${org ? 'mt-8' : 'mt-4'} divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200`}>
              {items.map((item, index) => (
                <details key={`${item?.q}-${index}`} className="group bg-white px-4 py-4">
                  <summary className={`flex cursor-pointer list-none justify-between gap-2 font-semibold text-slate-900 ${org ? 'text-base' : 'text-sm'}`}>
                    <span>{item.q}</span>
                    <span className="text-slate-400 transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  {item.a ? <div className={`${org ? 'mt-3 text-base' : 'mt-2 text-sm'} text-slate-600 whitespace-pre-line`}>{item.a}</div> : null}
                </details>
              ))}
            </div>
          ) : (
            <div className={tMuted}>질문과 답변을 입력해 주세요.</div>
          )}
        </PersonalSectionContainer>
      )
    }
    case 'ad-review': {
      const title = String(content?.title || '').trim() || '광고 심의필 · 준법 표기'
      const body =
        String(content?.body || '').trim()
        || '「소속사(또는 모집 주체)명」 준법감시인 심의필 제○호 (유효기간: 20××.××.×× ~ 20××.××.××)\n※ 소속사에서 받은 심의필·준법 표기 문구로 반드시 바꿔 주세요. 금융상품 광고·업무광고에 해당하는 공개물은 게시 전 심의를 마친 문구만 사용하는 것이 안전합니다.'
      // org: 위 본문 섹션(PersonalSectionContainer)이 이미 py-16·하단 구분선·mt-12 를 두므로
      // 여기에 mt-12 를 또 주면 마지막 콘텐츠와 심의필 블록 사이가 과하게 벌어짐.
      const wrapCls = org
        ? 'border-t border-white/10 bg-slate-950 px-6 py-12 text-center text-slate-300'
        : 'mt-6 border-t-2 border-slate-200 bg-slate-50 px-4 py-10 text-center text-slate-700 md:px-8'
      const titleCls = org ? 'text-base font-bold text-slate-300' : 'text-sm font-bold text-slate-800 md:text-base'
      const bodyCls = org
        ? 'mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-400 md:text-[15px]'
        : 'mt-3 whitespace-pre-line text-xs leading-relaxed text-slate-600 md:text-sm md:leading-relaxed'
      return (
        <section key={section.id} className={wrapCls}>
          <div className="mx-auto max-w-4xl">
            <h2 className={titleCls}>{title}</h2>
            <p className={bodyCls}>{body}</p>
          </div>
        </section>
      )
    }
    case 'cta': {
      const stack = templateCode === 'personal-cta.stack-v1'
      const btnStyle = { backgroundColor: 'var(--landing-accent, #1e3a8a)' }
      if (org) {
        return (
          <PersonalSectionContainer key={section.id} section={section} ctx={ctx} shell="cta">
            <h2 className="text-3xl font-black leading-tight md:text-5xl">{content.title || '상담이 필요하시면 편하게 문의해 주세요.'}</h2>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-white/80">{content.body || '간단한 정보만 남겨주셔도 빠르게 연락드리겠습니다.'}</p>
            <div className={`mt-8 flex flex-wrap gap-3 ${stack ? 'flex-col' : 'flex-col sm:flex-row'}`}>
              <button
                type="button"
                className={`rounded-full px-8 py-4 text-center text-base font-semibold text-slate-900 shadow-lg transition hover:bg-slate-100 ${ctx.isPreview ? 'cursor-not-allowed bg-white/40 text-slate-500' : 'bg-white'}`}
                onClick={() => {
                  if (!ctx.isPreview) ctx.setLeadOpen(true)
                }}
                disabled={ctx.isPreview}
              >
                상담 문의하기
              </button>
              <a
                className={`rounded-full border border-white/25 px-8 py-4 text-center text-base font-semibold ${ctx.contactHref ? 'hover:bg-white/10' : 'cursor-not-allowed opacity-40'}`}
                href={ctx.contactHref || undefined}
                target={ctx.kakaoChatUrl ? '_blank' : undefined}
                rel={ctx.kakaoChatUrl ? 'noreferrer' : undefined}
                onClick={(e) => {
                  if (!ctx.contactHref) e.preventDefault()
                }}
              >
                바로 연락하기
              </a>
              <Link className="rounded-full border border-white/25 px-8 py-4 text-center text-base font-semibold hover:bg-white/10" to={ctx.cardUrlPath}>
                모바일 명함 보기
              </Link>
            </div>
            <p className="mt-6 text-xs text-white/50">
              상담 문의하기로 남긴 정보는 상담 진행을 위해 최대 30일 보관 후 파기됩니다.
            </p>
          </PersonalSectionContainer>
        )
      }
      return (
        <div key={section.id} className="mt-8 rounded-2xl border bg-gradient-to-br from-slate-50 to-white p-6">
          <div className="text-lg font-bold text-slate-900">{content.title || '상담이 필요하시면 편하게 문의해 주세요.'}</div>
          <div className="mt-2 text-sm text-slate-600">{content.body || '간단한 정보만 남겨주셔도 빠르게 연락드리겠습니다.'}</div>
          <div className={`mt-5 flex gap-3 ${stack ? 'flex-col' : 'flex-col sm:flex-row'}`}>
            <button
              type="button"
              className={`rounded-lg px-5 py-3 text-center font-semibold text-white hover:opacity-90 ${ctx.isPreview ? 'cursor-not-allowed bg-slate-100 text-slate-400' : ''}`}
              style={ctx.isPreview ? undefined : btnStyle}
              onClick={() => {
                if (!ctx.isPreview) ctx.setLeadOpen(true)
              }}
              disabled={ctx.isPreview}
            >
              상담 문의하기
            </button>
            <a
              className={`rounded-lg px-5 py-3 text-center font-semibold border ${ctx.contactHref ? 'hover:bg-slate-50' : 'cursor-not-allowed opacity-40'}`}
              href={ctx.contactHref || undefined}
              target={ctx.kakaoChatUrl ? '_blank' : undefined}
              rel={ctx.kakaoChatUrl ? 'noreferrer' : undefined}
              onClick={(e) => {
                if (!ctx.contactHref) e.preventDefault()
              }}
            >
              바로 연락하기
            </a>
            <Link className="rounded-lg px-5 py-3 text-center font-semibold border hover:bg-slate-50" to={ctx.cardUrlPath}>
              모바일 명함 보기
            </Link>
          </div>
          <div className="mt-4 text-[11px] text-slate-500">
            상담 문의하기로 남긴 정보는 상담 진행을 위해 최대 30일 보관 후 파기됩니다.
          </div>
        </div>
      )
    }
    default:
      return null
  }
}
